import { useState } from "react";
import { usePageHeader } from "@/hooks/usePageHeader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  RefreshCw,
  Trash2,
  Plus,
  Clock,
  Users,
  FolderKanban,
  CalendarDays,
} from "lucide-react";
import {
  useClockifyStatus,
  useSyncProjects,
  useSyncUsers,
  useSyncTimeEntries,
  useComputeCarryOver,
} from "@/hooks/useClockify";
import {
  usePublicHolidays,
  useCreatePublicHoliday,
  useDeletePublicHoliday,
} from "@/hooks/usePublicHolidays";
import { formatDate } from "@/lib/date-utils";

export default function SettingsPage() {
  const currentYear = new Date().getFullYear();
  const [carryOverYear, setCarryOverYear] = useState(currentYear - 1);

  // Sync time entries date range
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = `${today.slice(0, 7)}-01`;
  const [syncStart, setSyncStart] = useState(firstOfMonth);
  const [syncEnd, setSyncEnd] = useState(today);

  // Clockify
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useClockifyStatus();
  const syncProjects = useSyncProjects();
  const syncUsers = useSyncUsers();
  const syncTimeEntries = useSyncTimeEntries();
  const computeCarryOver = useComputeCarryOver();

  // Public Holidays
  const { data: holidays = [], isLoading: holidaysLoading } =
    usePublicHolidays();
  const createHoliday = useCreatePublicHoliday();
  const deleteHoliday = useDeletePublicHoliday();
  const [newHolidayName, setNewHolidayName] = useState("");
  const [newHolidayDate, setNewHolidayDate] = useState("");

  const handleAddHoliday = () => {
    if (!newHolidayName.trim() || !newHolidayDate) return;
    createHoliday.mutate({ name: newHolidayName.trim(), date: newHolidayDate });
    setNewHolidayName("");
    setNewHolidayDate("");
  };

  usePageHeader({ title: "Settings" });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clockify Sync Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Clockify Integration
            </CardTitle>
            <CardDescription>
              Sync projects and time entries with Clockify
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {statusLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : status ? (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Projects: {status.synced_projects}/{status.total_projects}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Groups: {status.synced_project_groups}/
                      {status.total_project_groups}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Users: {status.synced_users}/{status.total_employees}
                    </span>
                  </div>
                  <div className="col-span-2 text-muted-foreground">
                    Time entries: {status.total_time_entries}
                    {status.last_sync &&
                      ` (last: ${formatDate(status.last_sync)})`}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => syncProjects.mutate()}
                    disabled={syncProjects.isPending}
                  >
                    {syncProjects.isPending && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Sync Projects
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => syncUsers.mutate()}
                    disabled={syncUsers.isPending}
                  >
                    {syncUsers.isPending && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Sync Users
                  </Button>
                </div>

                <div className="flex items-end gap-2 flex-wrap">
                  <div>
                    <Label htmlFor="sync-start" className="text-xs">
                      From
                    </Label>
                    <DatePicker
                      id="sync-start"
                      value={syncStart}
                      onChange={(v) => setSyncStart(v)}
                      className="w-44"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sync-end" className="text-xs">
                      To
                    </Label>
                    <DatePicker
                      id="sync-end"
                      value={syncEnd}
                      onChange={(v) => setSyncEnd(v)}
                      className="w-44"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      syncTimeEntries.mutate({
                        start: `${syncStart}T00:00:00Z`,
                        end: `${syncEnd}T23:59:59Z`,
                      })
                    }
                    disabled={
                      syncTimeEntries.isPending || !syncStart || !syncEnd
                    }
                  >
                    {syncTimeEntries.isPending && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Sync Time Entries
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-destructive">
                Failed to load status
                {statusError instanceof Error ? `: ${statusError.message}` : ""}
              </p>
            )}

            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-medium mb-2">
                Compute Overtime Carry-Over
              </h4>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={carryOverYear}
                  onChange={(e) => setCarryOverYear(parseInt(e.target.value))}
                  className="w-24"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => computeCarryOver.mutate(carryOverYear)}
                  disabled={computeCarryOver.isPending}
                >
                  {computeCarryOver.isPending && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Compute
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Public Holidays */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Public Holidays
            </CardTitle>
            <CardDescription>
              Configure public holidays for overtime calculation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label htmlFor="holiday-name" className="text-xs">
                  Name
                </Label>
                <Input
                  id="holiday-name"
                  value={newHolidayName}
                  onChange={(e) => setNewHolidayName(e.target.value)}
                  placeholder="e.g. Christmas"
                />
              </div>
              <div>
                <Label htmlFor="holiday-date" className="text-xs">
                  Date
                </Label>
                <DatePicker
                  id="holiday-date"
                  value={newHolidayDate}
                  onChange={(v) => setNewHolidayDate(v)}
                />
              </div>
              <Button
                size="icon"
                onClick={handleAddHoliday}
                disabled={
                  createHoliday.isPending ||
                  !newHolidayName.trim() ||
                  !newHolidayDate
                }
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {holidaysLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : holidays.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No public holidays configured
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {holidays.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50"
                  >
                    <div>
                      <span className="text-sm font-medium">{h.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {formatDate(h.date)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => deleteHoliday.mutate(h.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
